import "@/i18n";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollProgress from "@/components/effects/ScrollProgress";
import BackToTop from "@/components/effects/BackToTop";
import useLenis from "@/hooks/useLenis";
import { Toaster } from "sonner";

const Home = lazy(() => import("@/pages/Home"));
const Markets = lazy(() => import("@/pages/Markets"));
const Trade = lazy(() => import("@/pages/Trade"));
const Futures = lazy(() => import("@/pages/Futures"));
const CopyTrading = lazy(() => import("@/pages/CopyTrading"));
const PredictionMarket = lazy(() => import("@/pages/PredictionMarket"));
const Earn = lazy(() => import("@/pages/Earn"));
const NFT = lazy(() => import("@/pages/NFT"));
const Web3Wallet = lazy(() => import("@/pages/Web3Wallet"));
const APIPage = lazy(() => import("@/pages/APIPage"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const About = lazy(() => import("@/pages/About"));
const Blog = lazy(() => import("@/pages/Blog"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const Contact = lazy(() => import("@/pages/Contact"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.4 }}
      >
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/markets" element={<Markets />} />
            <Route path="/trade" element={<Trade />} />
            <Route path="/trade/:symbol" element={<Trade />} />
            <Route path="/futures" element={<Futures />} />
            <Route path="/copy-trading" element={<CopyTrading />} />
            <Route path="/prediction-market" element={<PredictionMarket />} />
            <Route path="/earn" element={<Earn />} />
            <Route path="/nft" element={<NFT />} />
            <Route path="/wallet" element={<Web3Wallet />} />
            <Route path="/api" element={<APIPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

const Shell = () => {
  useLenis();

  useEffect(() => {
    document.documentElement.classList.remove("member-theme");
  }, []);

  return (
    <div className="App">
      <Toaster
        theme="light"
        position="top-right"
        closeButton
        expand
        visibleToasts={4}
        gap={12}
        offset={18}
        richColors
        toastOptions={{
          duration: 4000,
          classNames: {
            toast: "mx-sonner-toast",
            title: "mx-sonner-title",
            description: "mx-sonner-desc",
          },
        }}
      />
      <ScrollProgress />
      <Navbar />
      <main className="relative min-h-screen">
        <AnimatedRoutes />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
